/**
 * Lightweight feature-flag helper.
 *
 * Resolution order for `isFlagOn(name)`:
 *   1. URL query param `?ff_<name>=on|off` — also persisted to
 *      localStorage so the override survives client-side navigation
 *      and reloads (drop `?ff_<name>=off` to switch back).
 *   2. localStorage `ff_<name>` (set by a prior query-param visit).
 *   3. Build-time default from an env var (`VITE_FF_<NAME>`), read via
 *      the static registry below.
 *   4. Default OFF.
 *
 * ⚠️ Vite gotcha: `import.meta.env[dynamicKey]` is NOT statically
 * replaced in a production build — only literal `import.meta.env.VITE_X`
 * references are inlined. So env defaults live in `FLAG_ENV_DEFAULTS`
 * with static references, never via dynamic key access.
 */

const truthy = (v: string | null | undefined): boolean => {
    if (v == null) return false;
    const s = v.toLowerCase();
    return s === "on" || s === "1" || s === "true";
};

/**
 * Build-time defaults per flag. Each entry MUST reference its env var
 * literally so Vite inlines it. Add a line here when introducing a flag.
 *
 * - `job_upload`: use Henry's job-based two-phase uploader
 *   (`POST /v2/submissions/upload` + poll). **ON by default** (approved for
 *   MVP); set `VITE_FF_JOB_UPLOAD=false` or visit `?ff_job_upload=off` to
 *   fall back to the legacy OR-1292 flow (`from-upload`, which now returns
 *   a job polled on the same `/v2/submissions/upload/{job_id}` endpoint).
 * - `tria_document` (OR-1424): the TRIA download row in Files → Generated
 *   Files. **OFF by default** — the Policy Form work (OR-1424 / OR-1455)
 *   shipped ungated (Policy download row, UW Information → Account Basics
 *   Mailing Address, Premium Selection → Coverage B/C sub-limits are all
 *   unconditional now); TRIA alone stays behind its own switch until it's
 *   ready. Visit `?ff_tria_document=on` to preview it (the override
 *   persists, so it survives navigation and reloads until you visit with
 *   `=off`).
 */
const FLAG_ENV_DEFAULTS: Record<string, boolean> = {
    job_upload: import.meta.env.VITE_FF_JOB_UPLOAD == null ? true : truthy(import.meta.env.VITE_FF_JOB_UPLOAD as string),
    tria_document: truthy(import.meta.env.VITE_FF_TRIA_DOCUMENT as string),
};

/**
 * Persist every `?ff_<name>=on|off` in the URL the app was opened with,
 * at load time. Doing it only when a screen asks for the flag loses it
 * when the first screen never does: opening a flagged link while logged
 * out lands on /login, and the redirect back drops the query string.
 */
export function persistFlagOverridesFromUrl(search: string = typeof window !== "undefined" ? window.location.search : ""): void {
    const params = new URLSearchParams(search);
    for (const [key, value] of params) {
        if (!key.startsWith("ff_")) continue;
        try {
            window.localStorage.setItem(key, truthy(value) ? "on" : "off");
        } catch {
            // localStorage unavailable (private mode) — ignore.
        }
    }
}

persistFlagOverridesFromUrl();

export function isFlagOn(name: string): boolean {
    const key = `ff_${name}`;

    if (typeof window !== "undefined") {
        // 1. Query-param override (persisted so it sticks across navigation).
        const param = new URLSearchParams(window.location.search).get(key);
        if (param != null) {
            const on = truthy(param);
            try {
                window.localStorage.setItem(key, on ? "on" : "off");
            } catch {
                // localStorage unavailable (private mode) — ignore.
            }
            return on;
        }
        // 2. localStorage override.
        try {
            const stored = window.localStorage.getItem(key);
            if (stored != null) return truthy(stored);
        } catch {
            // ignore
        }
    }

    // 3. Env default → 4. OFF.
    return FLAG_ENV_DEFAULTS[name] ?? false;
}
